import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { formatCurrency, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Building2, DoorOpen, Crown, DollarSign, TrendingUp, Loader2, 
  CheckCircle, Clock, Home, Key, BarChart3, PieChart, Activity,
  UserCheck, UserX, Mail, CreditCard, Percent
} from 'lucide-react';

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await adminAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load analytics</p>
      </div>
    );
  }

  const { overview, tenant_status, subscriptions, payments, landlord_details, recent_activity } = analytics;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="w-8 h-8 text-[#C85A42]" />
          Platform Analytics
        </h1>
        <p className="text-muted-foreground mt-1">Comprehensive overview of your U-DORM platform</p>
      </div>

      {/* Overview Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 border-indigo-200 dark:border-indigo-900">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-indigo-600 dark:text-indigo-400" />
            <p className="text-2xl font-bold mt-2">{overview.total_landlords}</p>
            <p className="text-xs text-muted-foreground">Landlords</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-200 dark:border-emerald-900">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-emerald-600 dark:text-emerald-400" />
            <p className="text-2xl font-bold mt-2">{overview.total_tenants}</p>
            <p className="text-xs text-muted-foreground">Tenants</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-200 dark:border-purple-900">
          <CardContent className="p-4 text-center">
            <Key className="w-6 h-6 mx-auto text-purple-600 dark:text-purple-400" />
            <p className="text-2xl font-bold mt-2">{overview.total_property_managers}</p>
            <p className="text-xs text-muted-foreground">Managers</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-200 dark:border-blue-900">
          <CardContent className="p-4 text-center">
            <Building2 className="w-6 h-6 mx-auto text-blue-600 dark:text-blue-400" />
            <p className="text-2xl font-bold mt-2">{overview.total_properties}</p>
            <p className="text-xs text-muted-foreground">Properties</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-200 dark:border-amber-900">
          <CardContent className="p-4 text-center">
            <DoorOpen className="w-6 h-6 mx-auto text-amber-600 dark:text-amber-400" />
            <p className="text-2xl font-bold mt-2">{overview.total_units}</p>
            <p className="text-xs text-muted-foreground">Total Units</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-rose-500/10 to-rose-500/5 border-rose-200 dark:border-rose-900">
          <CardContent className="p-4 text-center">
            <Percent className="w-6 h-6 mx-auto text-rose-600 dark:text-rose-400" />
            <p className="text-2xl font-bold mt-2">{overview.occupancy_rate}%</p>
            <p className="text-xs text-muted-foreground">Occupancy</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-[#C85A42]" />
              Tenant Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-green-600" />
                <span className="text-sm">Verified</span>
              </div>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                {tenant_status.verified}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm">Pending Verification</span>
              </div>
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                {tenant_status.pending_verification}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Pending Invitations</span>
              </div>
              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                {tenant_status.pending_invitations}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Analytics Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Crown className="w-5 h-5 text-amber-500" />
              Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Active Subscriptions</span>
              <span className="font-bold">{subscriptions.active}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Subscription Rate</span>
              <span className="font-bold">{subscriptions.subscription_rate}%</span>
            </div>
            <Progress value={subscriptions.subscription_rate} className="h-2" />
            <div className="pt-2 border-t">
              <p className="text-sm text-muted-foreground mb-2">By Tier:</p>
              <div className="space-y-2">
                {Object.entries(subscriptions.by_tier || {}).map(([tier, data]) => (
                  <div key={tier} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{tier}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{data.count}</Badge>
                      <span className="text-muted-foreground">{formatCurrency(data.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Analytics Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="w-5 h-5 text-green-600" />
              Rent Payments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Rent Collected</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                {formatCurrency(payments.total_rent_collected)}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Payments</span>
              <span className="font-semibold">{payments.total}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm">Verified</span>
              </div>
              <Badge className="bg-green-100 text-green-700">{payments.verified}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm">Pending</span>
              </div>
              <Badge className="bg-amber-100 text-amber-700">{payments.pending}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unit Occupancy */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-[#C85A42]" />
            Unit Occupancy Overview
          </CardTitle>
          <CardDescription>Distribution of occupied vs vacant units across all properties</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
              <DoorOpen className="w-8 h-8 mx-auto text-emerald-600 dark:text-emerald-400" />
              <p className="text-3xl font-bold mt-2 text-emerald-700 dark:text-emerald-400">{overview.occupied_units}</p>
              <p className="text-sm text-muted-foreground">Occupied Units</p>
            </div>
            <div className="text-center p-6 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <DoorOpen className="w-8 h-8 mx-auto text-amber-600 dark:text-amber-400" />
              <p className="text-3xl font-bold mt-2 text-amber-700 dark:text-amber-400">{overview.vacant_units}</p>
              <p className="text-sm text-muted-foreground">Vacant Units</p>
            </div>
            <div className="text-center p-6 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <Activity className="w-8 h-8 mx-auto text-blue-600 dark:text-blue-400" />
              <p className="text-3xl font-bold mt-2 text-blue-700 dark:text-blue-400">{overview.active_leases}</p>
              <p className="text-sm text-muted-foreground">Active Leases</p>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Occupancy Rate</span>
              <span className="text-sm font-bold">{overview.occupancy_rate}%</span>
            </div>
            <Progress value={overview.occupancy_rate} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#C85A42]" />
            Recent Activity (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg text-center">
              <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-400">{recent_activity.new_landlords_30d}</p>
              <p className="text-sm text-muted-foreground">New Landlords</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg text-center">
              <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{recent_activity.new_tenants_30d}</p>
              <p className="text-sm text-muted-foreground">New Tenants</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Landlord Details Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-[#C85A42]" />
            Landlord Performance
          </CardTitle>
          <CardDescription>Detailed breakdown of each landlord's portfolio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Landlord</th>
                  <th className="text-center p-3 font-medium">Tenants</th>
                  <th className="text-center p-3 font-medium">Properties</th>
                  <th className="text-center p-3 font-medium">Units</th>
                  <th className="text-center p-3 font-medium">Occupancy</th>
                  <th className="text-center p-3 font-medium">Subscription</th>
                </tr>
              </thead>
              <tbody>
                {landlord_details.map((landlord, index) => (
                  <tr key={landlord.user_id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {getInitials(landlord.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{landlord.name}</p>
                          <p className="text-xs text-muted-foreground">{landlord.email}</p>
                          {landlord.company_name && (
                            <p className="text-xs text-blue-600">{landlord.company_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                        {landlord.tenant_count}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="outline">{landlord.property_count}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="outline">{landlord.unit_count}</Badge>
                    </td>
                    <td className="p-3 text-center">
                      {landlord.unit_count > 0 ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {landlord.occupied_units}/{landlord.unit_count}
                          </span>
                          <Badge 
                            className={
                              landlord.occupied_units / landlord.unit_count >= 0.8 
                                ? "bg-green-100 text-green-700" 
                                : landlord.occupied_units / landlord.unit_count >= 0.5
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"
                            }
                          >
                            {Math.round((landlord.occupied_units / landlord.unit_count) * 100)}%
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {landlord.subscription_status === 'active' ? (
                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 capitalize">
                          <Crown className="w-3 h-3 mr-1" />
                          {landlord.subscription_tier || 'Active'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          No Subscription
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {landlord_details.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No landlords found
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
